import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { Grid, Field, Flex, Button, Segment, Divider } from 'nallian-shared-ui/lib/components';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
  CheckBoxFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { COMMUNITY_APP_FORM_NAME } from '../../../constants';
import ReadOnlyFieldValueComponent from '../../../../app/components/ReadOnlyFieldValueComponent';

const FeatureGrid = styled(Grid)`
  width: calc(100% + 0.5rem);
`;

const FeatureSegment = styled(Segment)`
  width: 100%;
`;

const createFeatureOption = f => ({ key: f.tag, value: f.tag, text: f.name });

const Feature = ({
  index,
  fields,
  field,
  features,
  submitProcessing,
  handleRemoveFieldClick,
  readOnly,
  editMode,
}) => {
  const { t } = useTranslation();

  const isNew = get(fields.get(index), 'isNew');

  const { appInstallationFeature, communityAppFeatureRoles } = useSelector(state =>
    get(state, `form.${COMMUNITY_APP_FORM_NAME}.values.communityAppFeatures[${index}]`, {})
  );

  const featureOptions = useMemo(() => {
    const allFields = fields.getAll();

    return features.map(createFeatureOption).filter(option => {
      const foundField = allFields.find(
        featureField => featureField.appInstallationFeature?.tag === option.value
      );

      return (
        !foundField ||
        (foundField &&
          index === allFields.findIndex(af => af.appInstallationFeature?.tag === option.value))
      );
    });
  }, [features, fields, index]);

  const roleOptions = useMemo(() => {
    const foundFeature = features.find(f => f.tag === appInstallationFeature?.tag);
    const selectedTags = communityAppFeatureRoles || [];

    return (foundFeature?.appInstallationFeatureRoles || [])
      // Obsolete roles can no longer be assigned, but a role already selected
      // before it became obsolete must stay visible so it keeps showing its name.
      .filter(r => !r.isObsolete || selectedTags.includes(r.tag))
      .map(r => ({
        key: r.tag,
        value: r.tag,
        text: r.name,
      }));
  }, [appInstallationFeature, communityAppFeatureRoles, features]);

  return (
    <FeatureSegment secondary>
      <FeatureGrid>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            search
            label={t('feature')}
            name={`${field}.appInstallationFeature.tag`}
            options={featureOptions}
            valueComponent={
              readOnly || (editMode && !isNew)
                ? ReadOnlyFieldValueComponent
                : SelectFieldValueComponent
            }
            disabled={submitProcessing}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('alias')}
            name={`${field}.alias`}
            valueComponent={readOnly ? ReadOnlyFieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            toggle
            label={t('requiresSubscription')}
            name={`${field}.requiresSubscription`}
            valueComponent={CheckBoxFieldValueComponent}
            disabled={readOnly || submitProcessing}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('url')}
            name={`${field}.url`}
            valueComponent={readOnly ? ReadOnlyFieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing}
          />
        </Grid.Column>
        {appInstallationFeature && (
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <Field
              fluid
              toggle
              multiple
              search
              label={t('roles')}
              name={`${field}.communityAppFeatureRoles`}
              valueComponent={readOnly ? ReadOnlyFieldValueComponent : SelectFieldValueComponent}
              options={roleOptions}
              disabled={submitProcessing}
            />
          </Grid.Column>
        )}
      </FeatureGrid>
      {!readOnly && (
        <>
          <Divider />
          <Flex fluid direction="column" alignItems="flex-end" margin="0 0.75rem 0">
            <Button
              linkbutton
              icon="close"
              type="button"
              tabindex="-1"
              content={t('removeFeature')}
              i={index}
              onClick={handleRemoveFieldClick}
            />
          </Flex>
        </>
      )}
    </FeatureSegment>
  );
};

Feature.propTypes = {
  fields: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  field: PropTypes.string.isRequired,
  submitProcessing: PropTypes.bool.isRequired,
  features: PropTypes.array.isRequired,
  handleRemoveFieldClick: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default Feature;
